import { type PointerEvent, type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import AnalyticsMermaidZoomControls from "./AnalyticsMermaidZoomControls";
import {
  clampScale,
  computeFitTransform,
  type ViewportTransform,
  zoomAtPoint,
} from "./utils/mermaidZoomTransform";

import { cn } from "@/lib/utils";

const SCALE_STEP = 0.2;
const FIT_RETRY_LIMIT = 12;

const AnalyticsMermaidZoomViewport = ({
  children,
  className,
  enabled = true,
  fitKey,
}: {
  children: ReactNode;
  className?: string;
  enabled?: boolean;
  /** When this value changes, the viewport re-fits the diagram to show it entirely. */
  fitKey?: string;
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState<ViewportTransform>({
    scale: 1,
    translateX: 0,
    translateY: 0,
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragOriginRef = useRef({ x: 0, y: 0, translateX: 0, translateY: 0 });
  const transformRef = useRef(transform);
  transformRef.current = transform;

  const fitToView = useCallback(() => {
    const canvas = canvasRef.current;
    const content = contentRef.current;
    if (!canvas || !content) {
      return false;
    }

    const nextTransform = computeFitTransform(canvas, content, transformRef.current.scale);
    if (!nextTransform) {
      return false;
    }

    setTransform(nextTransform);
    return true;
  }, []);

  const resetTransform = () => {
    fitToView();
  };

  const zoomBy = (delta: number) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const center = { x: rect.width / 2, y: rect.height / 2 };

    setTransform(current => {
      const nextScale = clampScale(current.scale + delta);
      return zoomAtPoint(current, center, nextScale);
    });
  };

  useEffect(() => {
    if (!enabled || !fitKey) {
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const tryFit = () => {
      if (cancelled) {
        return;
      }

      if (fitToView()) {
        return;
      }

      attempts += 1;
      if (attempts < FIT_RETRY_LIMIT) {
        requestAnimationFrame(tryFit);
      }
    };

    requestAnimationFrame(tryFit);

    return () => {
      cancelled = true;
    };
  }, [enabled, fitKey, fitToView]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !enabled) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();

      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const point = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      const delta = event.deltaY > 0 ? -SCALE_STEP : SCALE_STEP;

      setTransform(current => {
        const nextScale = clampScale(current.scale + delta);
        return zoomAtPoint(current, point, nextScale);
      });
    };

    viewport.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      viewport.removeEventListener("wheel", handleWheel);
    };
  }, [enabled]);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!enabled || event.button !== 0) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    setIsDragging(true);
    dragOriginRef.current = {
      x: event.clientX,
      y: event.clientY,
      translateX: transform.translateX,
      translateY: transform.translateY,
    };
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!isDragging) {
      return;
    }

    const origin = dragOriginRef.current;
    setTransform(current => ({
      ...current,
      translateX: origin.translateX + (event.clientX - origin.x),
      translateY: origin.translateY + (event.clientY - origin.y),
    }));
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!isDragging) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    setIsDragging(false);
  };

  if (!enabled) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      ref={viewportRef}
      className={cn(
        "relative overflow-hidden rounded-lg border border-gray-200 bg-white",
        className,
      )}
    >
      <AnalyticsMermaidZoomControls
        onFit={resetTransform}
        onZoomIn={() => zoomBy(SCALE_STEP)}
        onZoomOut={() => zoomBy(-SCALE_STEP)}
      />

      <div
        ref={canvasRef}
        className={cn(
          "absolute inset-4 touch-none overflow-hidden",
          isDragging ? "cursor-grabbing" : "cursor-grab",
        )}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={resetTransform}
      >
        <div
          ref={contentRef}
          className="absolute left-0 top-0 origin-top-left [&_svg]:block [&_svg]:h-auto [&_svg]:max-w-none"
          style={{
            transform: `translate(${transform.translateX}px, ${transform.translateY}px) scale(${transform.scale})`,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsMermaidZoomViewport;
